import Alert from '@mui/material/Alert'
import Container from '@mui/material/Container'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import KNLoader from 'components/KNLoader/KNLoader'
import { decodeJwt } from 'context/authentication/Auth.service'
import type { Company } from 'context/authentication/Company.types'
import { setSelectedCompany } from 'context/authentication/User.helpers'
import { UserContext } from 'context/authentication/UserContext'
import { TripDetailsContext } from 'context/trips/TripDetailsContext'
import { analyticsEvent, analyticsPageView } from 'global/helpers/analytics'
import { combineQueries } from 'global/helpers/queries'
import { getRequestErrorMessage } from 'global/helpers/requestError'
import { isMeaBranch } from 'global/helpers/regions'
import Footer from 'modules/Footer/Footer'
import TopNavigation from 'modules/TopNavigation/TopNavigation'
import { type ReactElement, useCallback, useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'
import TripAcceptance from 'screens/TripDashboard/TripAcceptance'
import { sendTripAcceptance, tripQueryOptions } from 'screens/TripDashboard/TripDashboard.service'
import type { TripData } from 'screens/TripDashboard/TripDashboard.types'
import LocationsMapLibreView from 'screens/TripDetails/MapView/LocationsMapLibreView'
import SequenceMapLibreView from 'screens/TripDetails/MapView/SequenceMapLibreView'
import ShipmentsView from 'screens/TripDetails/ShipmentsView/ShipmentsView'
import StopsView from 'screens/TripDetails/StopsView/StopsView'
import { isStopsViewAvailable } from 'screens/TripDetails/TripDetails.helpers'
import { tripLegsQueryOptions, tripLogsQueryOptions } from 'screens/TripDetails/TripDetails.service'
import TripLogs from 'screens/TripDetails/TripLogs'
import TripSummary from 'screens/TripDetails/TripSummary'
import ViewSwitcher from 'screens/TripDetails/ViewSwitcher'
import RequestAccountPromo from './RequestAccountPromo'
import { weblinkTokenQueryOptions } from './Weblink.service'

const combineTripQueries = combineQueries(['trip', 'legs'])
const combineSidebarQueries = combineQueries(['logs'])

const Weblink = (): ReactElement => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { t } = useTranslation()
  const { jwt } = useParams<{
    jwt: string
  }>()
  const queryClient = useQueryClient()
  const [tripDetailsState, tripDetailsDispatch] = useContext(TripDetailsContext)
  const { user } = useContext(UserContext)
  const history = useHistory()
  const tokenQuery = useQuery(weblinkTokenQueryOptions(jwt))
  const weblinkToken = tokenQuery.data?.token
  const tripCid = decodeJwt(weblinkToken ?? null)?.authorizations?.cid
  const tripQueries = useQueries({
    queries: [
      {
        ...tripQueryOptions(tripCid, weblinkToken),
        enabled: !!tripCid,
      },
      {
        ...tripLegsQueryOptions(tripCid, weblinkToken),
        enabled: !!tripCid,
      },
    ],
    combine: combineTripQueries,
  })
  const sidebarQueries = useQueries({
    queries: [
      {
        ...tripLogsQueryOptions(tripCid, weblinkToken),
        enabled: !!tripCid,
      },
    ],
    combine: combineSidebarQueries,
  })

  const getUserCompanyByGroupId = (groupId: string, companies: Company[] = []) =>
    companies.find((company) => company.groupIds.includes(groupId)) ?? null

  const redirectToLoggedInView = (trip: TripData) => {
    const relatedCompany = getUserCompanyByGroupId(trip.groupId, user?.companies)
    if (!relatedCompany) {
      return
    }
    setSelectedCompany(relatedCompany)
    history.push(`/trips/${trip.entityId}`)
  }

  const handleOnChange = useCallback(async (): Promise<void> => {
    if (!tripCid || !weblinkToken) {
      return
    }
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: tripQueryOptions(tripCid, weblinkToken).queryKey }),
      queryClient.invalidateQueries({ queryKey: tripLegsQueryOptions(tripCid, weblinkToken).queryKey }),
      queryClient.invalidateQueries({ queryKey: tripLogsQueryOptions(tripCid, weblinkToken).queryKey }),
    ])
  }, [queryClient, tripCid, weblinkToken])

  const tripAcceptanceMutation = useMutation({
    mutationFn: ({ tripCid, status }: { tripCid: string, status: 'ACCEPTED' | 'DECLINED' }) =>
      sendTripAcceptance(tripCid, { status }, weblinkToken),
    onSuccess: handleOnChange,
  })

  const handleTripAcceptanceChange = useCallback(async (updatedTrip: TripData, action: string): Promise<void> => {
    if (action === 'trip_accepted') {
      await tripAcceptanceMutation.mutateAsync({ tripCid: updatedTrip.entityId, status: 'ACCEPTED' })
      analyticsEvent('polestar_cs_trip_acceptance', ['accepted'])
    } else if (action === 'trip_declined') {
      await tripAcceptanceMutation.mutateAsync({ tripCid: updatedTrip.entityId, status: 'DECLINED' })
      analyticsEvent('polestar_cs_trip_acceptance', ['declined'])
    }
  }, [tripAcceptanceMutation])

  const getGeolocation = useCallback(() => {
    if (!navigator.geolocation) {
      return
    }
    if (localStorage.getItem('geolocation')) {
      return
    }
    if (!tripQueries.isSuccess) {
      return
    }
    if (!isMeaBranch(tripQueries.data.trip.logicalSender)) {
      return
    }

    navigator.geolocation.getCurrentPosition(
      () => {
        localStorage.setItem('geolocation', 'permission_granted')
        analyticsEvent('polestar_cs_geolocation', ['permission_granted'])
      },
      (error: GeolocationPositionError) => {
        switch (error.code) {
          case error.PERMISSION_DENIED:
            localStorage.setItem('geolocation', 'permission_denied')
            analyticsEvent('polestar_cs_geolocation', ['permission_denied'])
            break
          case error.POSITION_UNAVAILABLE:
            localStorage.setItem('geolocation', 'position_unavailable')
            analyticsEvent('polestar_cs_geolocation', ['position_unavailable'])
            break
          case error.TIMEOUT:
            localStorage.setItem('geolocation', 'timeout')
            analyticsEvent('polestar_cs_geolocation', ['timeout'])
            break
          default:
            localStorage.setItem('geolocation', 'unknown_error')
            analyticsEvent('polestar_cs_geolocation', ['unknown_error'])
            break
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
      },
    )
  }, [tripQueries])

  useEffect(() => {
    analyticsPageView('polestar/cs/weblink')
  }, [])

  useEffect(() => {
    if (!tripQueries.isSuccess) {
      return
    }

    if (user) {
      redirectToLoggedInView(tripQueries.data.trip)
    }

    if (isMobile) {
      getGeolocation()
      if (isStopsViewAvailable(tripQueries.data.trip)) {
        tripDetailsDispatch({ type: 'setDetailsView', payload: 'stops' })
      }
    }
  }, [isMobile, tripQueries])

  return (
    <Container
      maxWidth="xl"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
      }}
    >
      {tokenQuery.isPending && (
        <KNLoader>
          <Typography>{t('screens.cs.trip_details.loading')}</Typography>
        </KNLoader>
      )}
      {tokenQuery.isError && (
        <Alert severity="error">{t('errors.GenericError')}</Alert>
      )}
      {tokenQuery.isSuccess && (
        <Stack
          direction="column"
          sx={{
            minHeight: '100vh',
          }}
        >
          <TopNavigation />
          {tripQueries.isPending && (
            <KNLoader>
              <Typography>{t('screens.cs.trip_details.loading')}</Typography>
            </KNLoader>
          )}
          {tripQueries.isError && (
            <Alert severity="error">{getRequestErrorMessage(tripQueries.error)}</Alert>
          )}
          {tripQueries.isSuccess && (
            <Stack
              direction="column"
              spacing="2"
              alignItems="stretch"
              sx={{
                paddingTop: '4rem',
                paddingBottom: '1rem',
              }}
            >
              <TripAcceptance trip={tripQueries.data.trip} onChange={handleTripAcceptanceChange} />
              <Stack spacing={2} sx={{ flexGrow: 1 }}>
                <Stack
                  direction={{ xs: 'column-reverse', lg: 'row' }}
                  spacing={{ xs: 2, lg: 4 }}
                  alignItems="stretch"
                  sx={{
                    position: 'relative',
                    marginX: {
                      xs: '-0.5rem !important',
                      md: '0 !important',
                    },
                    flexGrow: 1,
                  }}
                >
                  {tripDetailsState.detailsView === 'shipments' && (
                    <ShipmentsView
                      trip={tripQueries.data.trip}
                      legs={tripQueries.data.legs}
                      weblinkToken={weblinkToken}
                    />
                  )}
                  {tripDetailsState.detailsView === 'stops' && (
                    <StopsView
                      trip={tripQueries.data.trip}
                      legs={tripQueries.data.legs}
                      weblinkToken={weblinkToken}
                    />
                  )}
                  {tripDetailsState.detailsView === 'sequence_map' && (
                    <SequenceMapLibreView
                      trip={tripQueries.data.trip}
                      legs={tripQueries.data.legs}
                      tripLogs={sidebarQueries.data?.logs ?? []}
                      weblinkToken={weblinkToken}
                    />
                  )}
                  {tripDetailsState.detailsView === 'locations_map' && (
                    <LocationsMapLibreView
                      trip={tripQueries.data.trip}
                      legs={tripQueries.data.legs}
                      tripLogs={sidebarQueries.data?.logs ?? []}
                      weblinkToken={weblinkToken}
                    />
                  )}
                  <Stack
                    spacing={2}
                    sx={{
                      position: { lg: 'sticky' },
                      maxWidth: { lg: '360px' },
                      width: { lg: '360px' },
                      height: { xs: 'auto', md: 'calc(100vh - 5rem)' },
                      maxHeight: { xs: '40rem', md: 'none' },
                      top: '4.5rem',
                    }}
                  >
                    {!isMobile && <ViewSwitcher stopsViewAvailable={isStopsViewAvailable(tripQueries.data.trip)} />}
                    <TripSummary
                      trip={tripQueries.data.trip}
                      legs={tripQueries.data.legs}
                      weblinkToken={weblinkToken}
                      onChange={handleOnChange}
                    />

                    {!isMobile && (
                      <Paper
                        sx={{
                          flexGrow: 1,
                          overflowY: 'hidden',
                        }}
                      >
                        {sidebarQueries.isPending && (
                          <KNLoader />
                        )}
                        {sidebarQueries.isSuccess && (
                          <Stack sx={{ height: '100%' }}>
                            <TripLogs
                              legs={tripQueries.data.legs}
                              tripLogs={sidebarQueries.data.logs ?? []}
                              stopsViewAvailable={isStopsViewAvailable(tripQueries.data.trip)}
                            />
                          </Stack>
                        )}
                      </Paper>
                    )}

                    {!isMobile && <RequestAccountPromo weblinkToken={weblinkToken!} />}
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          )}
          <Footer sx={{ marginTop: 'auto', marginBottom: '1rem' }}/>
        </Stack>
      )}
    </Container>
  )
}

export default Weblink
